import {
  ORG_MEMBERSHIP_ROLES,
  ORG_ROLE_LABELS,
  isOrgMembershipRole,
  type OrgMembershipRole,
} from "@/auth/membershipRoles";
import { labelOf } from "@/constants/uiLabels";
import { formatDateDE } from "@/utils/isoDate";

export type MemberStatus = "invited" | "active" | "disabled";

export type OrgMember = {
  membershipId: string;
  profileId: string;
  displayName: string | null;
  email: string | null;
  status: MemberStatus;
  roles: string[];
  invitedAt: string | null;
  activatedAt: string | null;
  disabledAt: string | null;
  createdAt: string | null;
};

/** Verzeichnis: keine E-Mail, keine Einladung, keine Deaktivierten. */
export type OrgDirectoryMember = {
  profileId: string;
  displayName: string | null;
  roles: string[];
};

export type MemberListFilter = "all" | OrgMembershipRole | "disabled";

export function toDirectoryMember(row: {
  profile_id: string;
  display_name: string | null;
  roles: string[] | null;
  email?: string | null;
}): OrgDirectoryMember {
  return {
    profileId: row.profile_id,
    displayName: row.display_name,
    roles: row.roles ?? [],
  };
}

export function sortOrgRoles(roles: readonly string[]): OrgMembershipRole[] {
  return ORG_MEMBERSHIP_ROLES.filter((role) => roles.includes(role));
}

export function roleLabel(role: string): string {
  return isOrgMembershipRole(role) ? ORG_ROLE_LABELS[role] : labelOf(ORG_ROLE_LABELS, role);
}

export function activeMemberCount(
  members: readonly { status: string }[],
): number {
  return members.filter((member) => member.status === "active").length;
}

export function activeCountLabel(count: number): string {
  return count === 1 ? "1 aktives Mitglied" : `${count} aktive Mitglieder`;
}

/** Deaktivierte Personen ans Ende, sonst die Reihenfolge der RPC behalten. */
export function sortMembersDisabledLast<T extends { status: string }>(
  members: readonly T[],
): T[] {
  const active = members.filter((member) => member.status !== "disabled");
  const disabled = members.filter((member) => member.status === "disabled");
  return [...active, ...disabled];
}

export function filterOrgMembers<T extends OrgMember>(
  members: readonly T[],
  query: string,
  filter: MemberListFilter,
): T[] {
  const needle = query.trim().toLowerCase();
  return sortMembersDisabledLast(members).filter((member) => {
    if (filter === "disabled") {
      if (member.status !== "disabled") return false;
    } else if (filter !== "all" && member.status === "disabled") {
      return false;
    }
    if (filter !== "all" && filter !== "disabled" && !member.roles.includes(filter)) {
      return false;
    }
    if (!needle) return true;
    const name = (member.displayName ?? "").toLowerCase();
    const email = (member.email ?? "").toLowerCase();
    return name.includes(needle) || email.includes(needle);
  });
}

export function filterDirectory<T extends OrgDirectoryMember>(
  members: readonly T[],
  query: string,
): T[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return [...members];
  return members.filter((member) =>
    (member.displayName ?? "").toLowerCase().includes(needle),
  );
}

/** Zeilenlabel: Name, optional E-Mail, Rollen, bei Bedarf „Deaktiviert“. */
export function memberRowAccessibilityLabel(input: {
  name: string;
  email?: string | null;
  roleLabels: readonly string[];
  includeEmail?: boolean;
  deactivated?: boolean;
}): string {
  const roles = input.roleLabels.length > 0 ? input.roleLabels.join(", ") : "keine";
  const email =
    input.includeEmail && input.email?.trim() ? `, ${input.email.trim()}` : "";
  const deactivated = input.deactivated ? ", Deaktiviert" : "";
  return `${input.name}${email}, Rollen: ${roles}${deactivated}`;
}

export function formatMemberSince(iso: string | null): string | null {
  if (!iso) return null;
  const formatted = formatDateDE(iso);
  return formatted === "–" ? null : formatted;
}
